import { useMemo, useRef, useState } from "react"
import {
    Alert,
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material"
import { If, Else } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import Iconify from "minimals-template/components/Iconify"
import { buildFeaturePackDiff, parseFeaturePackIds, resolveFeaturePackImport } from "../auth-bundle-feature-pack"
import { FeaturePackDiffRow } from "./feature-pack-diff-row"
import { FeaturePackEmptyPreview } from "./feature-pack-empty-preview"

const MODE_MERGE = "merge"
const MODE_REPLACE = "replace"

export function FeaturePackImportDialog({ current = [], features = [], ok, cancel }) {
    const inputRef = useRef(null)
    const [mode, setMode] = useState(MODE_MERGE)
    const [packName, setPackName] = useState("")
    const [packIds, setPackIds] = useState([])
    const [error, setError] = useState("")
    const diff = useMemo(buildDiff, [current, packIds, mode])
    const diffRows = useMemo(buildDiffRows, [diff])
    const resultCount = diff.resultIds.length
    const canImport = packIds.length > 0

    return (
        <Box data-cy="feature-pack-import-dialog">
            <DialogTitle>Import pack into bundle</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Select a JSON pack exported from an auth bundle or feature flag workflow. The pack's feature
                        flags can be merged into this bundle or used to replace the bundle's current flag set.
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            bgcolor: "background.neutral",
                        }}
                    >
                        <Iconify icon="mdi:code-json" width={24} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2">
                                <If value={packName}>
                                    {packName}
                                    <Else>No pack selected</Else>
                                </If>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {packIds.length} feature flags
                            </Typography>
                        </Box>
                        <Button component="label" size="small" variant="outlined">
                            Choose file
                            <input
                                ref={inputRef}
                                hidden
                                type="file"
                                accept="application/json,.json"
                                onChange={handleFileChange}
                                data-cy="feature-pack-file-input"
                            />
                        </Button>
                    </Box>
                    <If value={error}>
                        <Alert severity="error">{error}</Alert>
                    </If>
                    <ToggleButtonGroup
                        exclusive
                        fullWidth
                        size="small"
                        value={mode}
                        onChange={handleModeChange}
                        data-cy="feature-pack-import-mode"
                    >
                        <ToggleButton value={MODE_MERGE}>Merge</ToggleButton>
                        <ToggleButton value={MODE_REPLACE}>Replace</ToggleButton>
                    </ToggleButtonGroup>
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="overline" color="text.disabled">
                                Preview
                            </Typography>
                            <Box flex={1} />
                            <Typography variant="caption" color="success.dark">
                                +{diff.added.length} added
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                                {diff.kept.length} kept
                            </Typography>
                            <Typography variant="caption" color="error.dark">
                                {diff.removed.length} removed
                            </Typography>
                        </Stack>
                        <Box
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                overflow: "auto",
                                maxHeight: 220,
                            }}
                        >
                            <Repeat
                                list={diffRows}
                                item={<FeaturePackDiffRow />}
                                keyFn="label"
                                fallback={<FeaturePackEmptyPreview />}
                            />
                        </Box>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    Bundle will have {resultCount} feature flags after importing.
                </Typography>
                <Button onClick={cancel} color="primary" data-cy="dialogActionsCancelButton">
                    Cancel
                </Button>
                <Button
                    onClick={handleImport}
                    variant="contained"
                    disabled={!canImport}
                    startIcon={<Iconify icon="eva:checkmark-fill" />}
                    data-cy="dialogActionsOkButton"
                >
                    Apply import
                </Button>
            </DialogActions>
        </Box>
    )

    function buildDiff() {
        return buildFeaturePackDiff({ current, packIds, mode })
    }

    function buildDiffRows() {
        return [
            ...diff.added.map(createAddedRow),
            ...diff.kept.map(createKeptRow),
            ...diff.removed.map(createRemovedRow),
        ]
    }

    function createAddedRow(id) {
        return {
            label: `feature-${id}`,
            status: "ADD",
            icon: "eva:plus-fill",
            color: "success.dark",
            bgcolor: "success.lighter",
        }
    }

    function createKeptRow(id) {
        return {
            label: `feature-${id}`,
            status: "KEPT",
            icon: "mdi:equal",
            color: "text.disabled",
            bgcolor: "background.paper",
        }
    }

    function createRemovedRow(id) {
        return {
            label: `feature-${id}`,
            status: "REMOVE",
            icon: "mdi:minus",
            color: "error.dark",
            bgcolor: "error.lighter",
        }
    }

    function handleModeChange(event, nextMode) {
        if (!nextMode) return
        setMode(nextMode)
    }

    function handleFileChange(event) {
        const file = event.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = handleFileRead
        reader.readAsText(file)
        event.target.value = ""

        function handleFileRead(readerEvent) {
            try {
                const parsed = JSON.parse(readerEvent.target?.result || "")
                const ids = parseFeaturePackIds(parsed)
                setPackName(file.name)
                setPackIds(ids)
                setError(getPackError(ids))
            } catch (readError) {
                console.error(readError)
                setPackName(file.name)
                setPackIds([])
                setError("That file is not a valid JSON feature pack.")
            }
        }
    }

    function handleImport() {
        if (!canImport) return
        ok(resolveFeaturePackImport({ current, packIds, features, mode }))
    }

    function getPackError(ids) {
        if (ids.length > 0) return ""
        return "That file does not contain any feature flags."
    }
}
